<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Banner,
  BannerVariant,
  Button,
  ButtonGroup,
  Form,
  Heading,
  Listbox,
  ListboxOption,
} from '@app/customer/Components';
import { AppLayout } from '@app/customer/Layouts';
import { useForm } from '@inertiajs/vue3';
import { useBrandColor } from '@app/customer/Composables/useBrandColor';
import { type Types } from '@app/shared/types/generated-v2';
import { paymentMethodService } from '@app/customer/Services';
import { useEmbeddedApp } from '@app/customer/Composables/useEmbeddedApp';
import { useApi } from '@app/customer/Composables/useApi';

type ListPaymentMethodsViewModel = Types['App.Http.ViewModels.Customer.ListPaymentMethodsViewModel'];

const { t } = useI18n();

const props = defineProps<ListPaymentMethodsViewModel>();

const buttonColor = useBrandColor();
const { getPaymentMethodLogo } = paymentMethodService();

const { iframeHostBaseUrl, isEmbedded, navigateIFrameHostToUrl } = useEmbeddedApp();

const form = useForm({
  payment_method: props.selectedPaymentMethod?.id || null,
  embedded_host_url: null as string | null,
  domain: props.domain,
  locale: props.locale,
});

const filteredPaymentMethods = computed((): Types['App.Models.ViewModels.Customer.PaymentMethodViewModel'][] => {
  const canUseApplePay = !isEmbedded && window.ApplePaySession && window.ApplePaySession.canMakePayments();

  return props.paymentMethods.filter((method: Types['App.Models.ViewModels.Customer.PaymentMethodViewModel']): boolean => {
    return !(method.systemName === 'applepay' && !canUseApplePay);
  });
});

watch(iframeHostBaseUrl, (): void => {
  if (iframeHostBaseUrl.value) {
    form.embedded_host_url = iframeHostBaseUrl.value;
  }
}, { immediate: true });

const loading = ref(false);

async function submitForm(): Promise<void> {
  loading.value = true;

  try {
    const response = await useApi().post(route('api.v1.intent.payment-method-update', props.returnOrderIntent), form.data());

    if (isEmbedded) {
      navigateIFrameHostToUrl(response.data.redirect_url);
    } else {
      window.location.href = response.data.redirect_url;
    }
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <AppLayout>
    <div class="mx-auto">
      <div class="flex flex-col justify-between border-b border-slate-100 pb-6 sm:flex-row sm:space-x-4">
        <Heading>
          {{ t('customer.payment:title') }}
        </Heading>
      </div>

      <div
        v-if="paymentStatus"
        class="mb-8 mt-8"
      >
        <Banner :variant="BannerVariant.Warning">
          {{ t('customer.payment:unable-to-process') }}
        </Banner>
      </div>

      <div
        v-if="form.errors.payment_method"
        class="mb-5"
      >
        <Banner :variant="BannerVariant.Critical">
          {{ t('customer.payment:is-required') }}
        </Banner>
      </div>

      <Form
        class="mt-4"
        @submit="submitForm"
      >
        <Listbox>
          <template
            v-for="paymentMethod in filteredPaymentMethods"
            :key="paymentMethod.id"
          >
            <ListboxOption
              v-model="form.payment_method"
              :label="paymentMethod.name"
              :value="paymentMethod.id"
              :image-url="getPaymentMethodLogo(paymentMethod)"
              :alt="paymentMethod.name"
            />
          </template>
        </Listbox>

        <div class="ml-auto mt-12">
          <ButtonGroup>
            <Button
              full-width
              :href="$route('customer.intent.shipping-method', returnOrderIntent)"
            >
              {{ t('customer.payment:back-button') }}
            </Button>

            <Button
              :color="buttonColor"
              full-width
              type="submit"
              name="submit_action"
              value="payment_method"
              :disabled="form.payment_method === null || loading"
            >
              {{ t('customer.payment:pay-button', { price: price }) }}
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </div>
  </AppLayout>
</template>
